/**
 * Button — the primary action element.
 *
 * Variants:
 *   primary   — Aurora gradient, used for the main CTA per surface
 *   ghost     — outlined, used for secondary actions
 *   toolbar   — input-height neutral button for dense control rows
 *   subtle    — flat tinted, used inside dense surfaces (toolbars)
 *   danger    — destructive actions
 *   link      — text-only, no padding/border
 *
 * Sizes: sm | md | lg
 *
 * Built on Radix Slot so consumers can wrap a Link without losing styles:
 *   <Button asChild><Link to="/x">…</Link></Button>
 */
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const buttonStyles = cva(
  // Base — every variant inherits these.
  [
    "inline-flex items-center justify-center gap-2",
    "font-semibold leading-none whitespace-nowrap",
    "rounded-xl border border-transparent",
    "transition-[transform,box-shadow,background-color,color] duration-150",
    "active:translate-y-[0.5px]",
    "disabled:opacity-50 disabled:pointer-events-none",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-0",
  ],
  {
    variants: {
      variant: {
        primary: [
          "text-white bg-aurora-grad",
          "shadow-primary-glow",
          "hover:brightness-110",
        ],
        ghost: ["bg-surface text-fg border-line-strong", "hover:bg-surface-2"],
        toolbar: ["bg-surface text-fg border-line", "hover:bg-surface-2"],
        subtle: [
          "bg-surface-tint text-fg-dim",
          "hover:bg-surface-2 hover:text-fg",
        ],
        danger: ["text-white bg-rose", "hover:brightness-110"],
        link: ["text-violet underline underline-offset-4", "hover:opacity-80"],
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10 p-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends
    ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
  /** Render as the immediate child (e.g. an <a>) instead of a <button>. */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonStyles({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";
